import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormSubmission } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

const IHSPSection: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { setValue, getValues } = useFormContext<FormSubmission>();
  const [isReady, setIsReady] = useState(false);
  const { language } = useLanguage();

  const FORM_SRC = language === 'es' ? '/ihsp_es.html' : '/ihsp.html';

  const isCheckbox = (el: Element): el is HTMLInputElement =>
    el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'checkbox';

  // Collect all field values from iframe → form state
  const collectFromIframe = useCallback(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;

    const flat: Record<string, string | boolean> = {};
    doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input[id], textarea[id], select[id]'
    ).forEach((el) => {
      if (!el.id) return;
      if (isCheckbox(el)) {
        flat[el.id] = el.checked;
      } else {
        flat[el.id] = el.value ?? '';
      }
    });

    setValue('ihsp_flat', flat, { shouldDirty: true });
  }, [setValue]);

  // Push existing form values back into the iframe on load
  const applyToIframe = useCallback(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;

    const setField = (id: string, value: string | boolean | undefined) => {
      const el = doc.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
      if (!el) return;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        el.checked = !!value;
      } else {
        (el as HTMLInputElement).value = value == null ? '' : String(value);
      }
    };

    // Restore all previously saved field values from the flat map first
    const savedFlat = getValues('ihsp_flat');
    if (savedFlat) {
      Object.entries(savedFlat).forEach(([id, val]) => {
        setField(id, val);
      });
    }

    // Then overlay member info (always from the authoritative source)
    const info = getValues('memberInfo');
    setField('member_name', `${info?.firstName || ''} ${info?.lastName || ''}`.trim());

    // Set client signature image (img element — needs separate handling)
    const sig = getValues('signature');
    if (sig?.imageData) {
      const imgEl = doc.getElementById('client_signature_image') as HTMLImageElement | null;
      if (imgEl) {
        imgEl.src = sig.imageData;
        imgEl.style.display = 'block';
      }
    }
    if (sig?.signedDate) {
      setField('client_sig_date', sig.signedDate);
    }
  }, [getValues]);

  // Listen for changes inside the iframe
  useEffect(() => {
    if (!isReady) return;
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;
    const handler = () => collectFromIframe();
    doc.addEventListener('input', handler, true);
    doc.addEventListener('change', handler, true);
    return () => {
      doc.removeEventListener('input', handler, true);
      doc.removeEventListener('change', handler, true);
    };
  }, [collectFromIframe, isReady]);

  const handleLoad = () => {
    setIsReady(true);
    applyToIframe();
    collectFromIframe();
  };

  const clearCheckboxes = () => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;
    doc.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => { cb.checked = false; });
    collectFromIframe();
  };

  const clearAllFields = () => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;
    doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input[id], textarea[id], select[id]'
    ).forEach((el) => {
      if (el instanceof HTMLInputElement && el.type === 'checkbox') el.checked = false;
      else (el as HTMLInputElement).value = '';
    });
    collectFromIframe();
  };

  return (
    <div className="space-y-4">
      <div className="rounded border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900">
        {language === 'es'
          ? 'Complete el Plan de Apoyo de Vivienda (IHSP) a continuación. La información del miembro se completa previamente desde el paso anterior. Los valores se sincronizan automáticamente y se usan para generar el PDF.'
          : 'Fill the Individualized Housing Support Plan (IHSP) below. Member info is pre-filled from the previous step. Values are auto-synced and used to generate the PDF.'}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={clearCheckboxes}
          className="rounded bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-200"
        >
          {language === 'es' ? 'Limpiar Casillas' : 'Clear Checkboxes'}
        </button>
        <button
          type="button"
          onClick={clearAllFields}
          className="rounded bg-red-100 px-3 py-1.5 text-sm font-medium text-red-900 hover:bg-red-200"
        >
          {language === 'es' ? 'Limpiar Todo' : 'Clear All Fields'}
        </button>
      </div>

      <iframe
        key={FORM_SRC}
        ref={iframeRef}
        title="Housing Support Plan Form"
        src={FORM_SRC}
        onLoad={handleLoad}
        className="h-[80vh] w-full rounded border border-gray-300 bg-white"
      />
    </div>
  );
};

export default IHSPSection;
